import React, { useState } from 'react';
import { Check, Star, ArrowRight, Plus } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { services, addOns } from '../data/services';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';

const Services = () => {
  const [selectedTab, setSelectedTab] = useState('all');
  const { addToCart } = useCart();

  const categories = [
    { id: 'all', name: 'All Services' },
    { id: 'packs', name: 'Application Packs' },
    { id: 'monthly', name: 'Monthly Services' },
    { id: 'addons', name: 'Add-Ons' }
  ];

  const filteredServices = selectedTab === 'all' 
    ? services 
    : selectedTab === 'packs'
    ? services.filter(s => s.category === 'Application Packs' || s.category === 'Career Launchpad')
    : selectedTab === 'monthly'
    ? services.filter(s => s.category === 'Monthly Services')
    : [];

  const handleBuyNow = (service) => {
    // Add to cart instead of direct redirect
    addToCart(service);
    toast.success(`${service.name} added to selections!`, {
      description: 'Review your selections in the cart.',
    });
  };

  return (
    <section id="services" className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-slate-50 via-white to-teal-50">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-12">
          <span className="inline-block px-4 py-2 bg-teal-100 text-teal-800 rounded-full text-sm font-semibold mb-4">
            Pricing
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Services & Pricing
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
            Whether you need a short burst of momentum or full white-glove management, each option delivers focused, human-led execution.
          </p>
        </div>

        {/* Category Tabs */}
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => setSelectedTab(category.id)}
              className={`px-6 py-3 rounded-full font-semibold transition-all ${
                selectedTab === category.id
                  ? 'bg-teal-700 text-white shadow-lg'
                  : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
              }`}
            >
              {category.name}
            </button>
          ))}
        </div>

        {/* Services Grid */}
        {selectedTab !== 'addons' && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {filteredServices.map((service) => (
              <Card
                key={service.id}
                className={`relative hover:shadow-2xl transition-all border-2 ${
                  service.highlighted
                    ? 'border-teal-500 shadow-xl scale-105'
                    : 'border-gray-200'
                }`}
              >
                {service.highlighted && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <Badge className="bg-teal-700 text-white px-4 py-1 flex items-center space-x-1">
                      <Star size={14} fill="currentColor" />
                      <span>Popular</span>
                    </Badge>
                  </div>
                )}
                
                <CardHeader className="pb-4">
                  <div className="text-sm font-semibold text-teal-700 mb-2">{service.category}</div>
                  <CardTitle className="text-2xl mb-2">{service.name}</CardTitle>
                  <CardDescription className="text-gray-600">{service.description}</CardDescription>
                  
                  <div className="mt-4">
                    <div className="flex items-baseline">
                      <span className="text-4xl font-bold text-gray-900">${service.price}</span>
                      {service.frequency && (
                        <span className="text-gray-600 ml-2">/ {service.frequency}</span>
                      )}
                      {!service.frequency && (
                        <span className="text-gray-600 ml-2">one-time</span>
                      )}
                    </div>
                    {service.commitmentPrice && (
                      <div className="text-sm text-gray-600 mt-1">
                        ${service.commitmentPrice}/mo with {service.commitmentTerm}
                      </div>
                    )}
                    {service.packagePrice && (
                      <div className="text-sm text-gray-600 mt-1">
                        ${service.packagePrice} for {service.packageTerm}
                      </div>
                    )}
                  </div>
                </CardHeader>

                <CardContent className="pb-6">
                  <div className="space-y-3 mb-6">
                    {service.features.map((feature, index) => (
                      <div key={index} className="flex items-start space-x-3">
                        <Check className="text-teal-700 flex-shrink-0 mt-0.5" size={18} />
                        <span className="text-sm text-gray-700">{feature}</span>
                      </div>
                    ))}
                  </div>
                  
                  <div className="bg-teal-50 rounded-lg p-3 border border-teal-200">
                    <div className="text-xs font-semibold text-teal-800 mb-1">Best For:</div>
                    <div className="text-sm text-gray-700">{service.bestFor}</div>
                  </div>
                </CardContent>

                <CardFooter>
                  <Button
                    onClick={() => handleBuyNow(service)}
                    className={`w-full group ${
                      service.highlighted
                        ? 'bg-brand-green-700 hover:bg-brand-green-800'
                        : 'bg-gray-900 hover:bg-gray-800'
                    } text-white py-6`}
                  >
                    <Plus className="mr-2" size={20} />
                    Add to Selections
                    <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={18} />
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>
        )}

        {/* Add-Ons Section */}
        {(selectedTab === 'all' || selectedTab === 'addons') && (
          <div className="mt-16">
            <div className="text-center mb-12">
              <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Enhance Your Service
              </h3>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Add-on services to maximize your job search effectiveness
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {addOns.map((addon) => (
                <Card key={addon.id} className="border-2 border-gray-200 hover:border-teal-300 hover:shadow-lg transition-all">
                  <CardHeader>
                    <CardTitle className="text-xl">{addon.name}</CardTitle>
                    <CardDescription className="text-gray-600">{addon.description}</CardDescription>
                    <div className="mt-3">
                      <div className="flex items-baseline">
                        <span className="text-3xl font-bold text-gray-900">${addon.price}</span>
                        {addon.maxPrice && (
                          <span className="text-gray-600 ml-1">– ${addon.maxPrice}</span>
                        )}
                      </div>
                      {addon.bundlePrice && (
                        <div className="text-sm text-gray-600 mt-1">
                          ${addon.bundlePrice}{addon.bundleMax && `–$${addon.bundleMax}`} for {addon.bundleTerm}
                        </div>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {addon.features.map((feature, index) => (
                        <div key={index} className="flex items-start space-x-2">
                          <Check className="text-teal-700 flex-shrink-0 mt-0.5" size={16} />
                          <span className="text-sm text-gray-700">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                  <CardFooter>
                    <Button
                      onClick={() => handleBuyNow(addon)}
                      variant="outline"
                      className="w-full border-2 border-brand-green-700 text-brand-green-700 hover:bg-brand-green-700 hover:text-white"
                    >
                      <Plus className="mr-2" size={18} />
                      Add to Selections
                    </Button>
                  </CardFooter>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Note about PayPal */}
        <div className="mt-12 text-center">
          <div className="inline-block bg-blue-50 border border-blue-200 rounded-xl p-6 max-w-2xl">
            <p className="text-gray-700">
              <span className="font-semibold text-gray-900">Secure Payment:</span> All transactions are processed securely through PayPal. After purchase, you'll receive an email with next steps to begin your application process.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Services;